import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

export interface UsePurchasesOptions {
  leagueId?: number;
  filters?: Record<string, unknown>;
  page?: number;
  pageSize?: number;
}

export function usePurchases({
  leagueId,
  filters = {},
  page = 0,
  pageSize = 25,
}: UsePurchasesOptions) {
  const cleanFilters = useMemo(() => {
    const cleaned: Record<string, unknown> = {
      ...filters,
      page,
      pageSize,
    };

    Object.keys(cleaned).forEach(key => {
      const value = cleaned[key];
      if (value === undefined || value === null || value === '') {
        delete cleaned[key];
      }
    });

    if (cleaned.teamIds && Array.isArray(cleaned.teamIds)) {
      cleaned.teamIds = (cleaned.teamIds as any[])
        .map(id => Number(id))
        .filter(id => !Number.isNaN(id));
      if ((cleaned.teamIds as number[]).length === 0) {
        delete cleaned.teamIds;
      }
    }

    if (cleaned.status && Array.isArray(cleaned.status)) {
      cleaned.status = (cleaned.status as string[]).filter(Boolean);
      if ((cleaned.status as string[]).length === 0) {
        delete cleaned.status;
      }
    }

    if (cleaned.hoursBack !== undefined && cleaned.hoursBack !== null) {
      const hoursValue = Number(cleaned.hoursBack);
      if (Number.isNaN(hoursValue) || hoursValue <= 0) {
        delete cleaned.hoursBack;
      } else {
        cleaned.hoursBack = hoursValue;
      }
    }

    return cleaned;
  }, [filters, page, pageSize]);

  const queryKey = ['purchases', leagueId, cleanFilters];
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!leagueId) {
        return null;
      }
      const response = await apiClient.post(`/purchases/${leagueId}`, cleanFilters);
      return response.data;
    },
    enabled: !!leagueId,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  };
}

